import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppBranding } from '../types/branding';
import brandingAPI, { normalizeBranding } from '../services/brandingService';
import { DEFAULT_BRANDING } from '../constants/branding';

interface BrandingContextValue {
  branding: AppBranding;
  loading: boolean;
  refreshBranding: () => Promise<void>;
  setBranding: (data: AppBranding) => void;
}

const fallbackBranding = normalizeBranding({
  site_title: DEFAULT_BRANDING.title,
  site_short_title: DEFAULT_BRANDING.shortTitle,
  site_subtitle: DEFAULT_BRANDING.subtitle,
  logo_path: null,
  logo_url: DEFAULT_BRANDING.logoUrl,
  copyright_text: DEFAULT_BRANDING.copyright,
});

const BrandingContext = createContext<BrandingContextValue>({
  branding: fallbackBranding,
  loading: true,
  refreshBranding: async () => {},
  setBranding: () => {},
});

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBrandingState] = useState<AppBranding>(fallbackBranding);
  const [loading, setLoading] = useState(true);

  const refreshBranding = useCallback(async () => {
    try {
      const response = await brandingAPI.getBranding();
      if (response.success) {
        setBrandingState(response.data);
      }
    } catch {
      setBrandingState(fallbackBranding);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  useEffect(() => {
    document.title = branding.site_title;
  }, [branding.site_title]);

  const value = useMemo(
    () => ({
      branding,
      loading,
      refreshBranding,
      setBranding: setBrandingState,
    }),
    [branding, loading, refreshBranding]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext);
